import React from 'react'
import {
  Badge,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Switch,
  Tooltip,
  Typography,
  Avatar,
  Divider,
  Modal,
  Button,
  InputNumber
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClinicMedical,
  faCog,
  faMoneyBill,
  faBuilding,
  faCreditCard,
  faCoins,
  faHeartPulse,
  faInfoCircle,
  faHospital,
  faUserMd,
  faSchool,
  faBriefcase,
  faWheelchair,
  faFileContract
} from '@fortawesome/free-solid-svg-icons'
import { faEdit } from '@fortawesome/free-regular-svg-icons'
import { hexToRGBA, specialFieldValidation } from '../../../../lib/utils'
import { imageOverlayStyle } from '../../utils/style-utils'
import {
  CLINIC_FORM_CATEGORY_ICONS,
  CLINIC_FORM_CATEGORY_COLORS,
  CLINIC_FORM_CATEGORY_LABELS
} from '../../config/form-categories'
import Templates from '../../utils/templates'
import store from '../../../../../store'
import { clinicTabsEnums } from '../../config/clinic-tabs-enums'
import TermsConditionsModal from './terms-conditions'

const { Title, Text } = Typography

export const createBasicInformationTab = ({
  token,
  unfilledFields,
  inputRef,
  handleFileChange,
  handleUploadClick,
  image,
  handleIsActive,
  loading,
  basicInfoForm,
  specialFormState,
  setSpecialFormState,
  handleFormSubmit,
  handleValuesChange,
  recordData,
  setRecordData,
  newRecord,
  // Terms and Conditions Modal props
  termsModalVisible,
  setTermsModalVisible
}) => {
  const coreState = store.getState().core
  const paymentMethods = coreState?.entity?.configs?.paymentMethods || []

  const getPaymentIcon = (method) => {
    switch (method.toLowerCase()) {
      case 'card':
        return faCreditCard
      case 'cash':
        return faCoins
      case 'medical aid':
        return faHeartPulse
      default:
        return faMoneyBill
    }
  }

  return {
    key: clinicTabsEnums.BASIC_INFORMATION,
    label: (
      <span>
        <FontAwesomeIcon
          icon={CLINIC_FORM_CATEGORY_ICONS.BASIC_INFORMATION}
          style={{ marginRight: '8px', color: CLINIC_FORM_CATEGORY_COLORS.BASIC_INFORMATION }}
        />
        {CLINIC_FORM_CATEGORY_LABELS.BASIC_INFORMATION}
        <Badge count={unfilledFields[1]} style={{ marginLeft: '8px', backgroundColor: token.colorError }} />
      </span>
    ),
    children: (
      <Form
        onFinish={(values) => {
          handleFormSubmit(values)
        }}
        onFinishFailed={(errorInfo) => {
          Modal.error({
            title: 'Form Validation Error',
            content: errorInfo.errorFields[0].errors[0]
          })
        }}
        name='form'
        form={basicInfoForm}
        initialValues={{
          ...Templates.dataTemplate(),
          ...recordData
        }}
        layout='vertical'
        onValuesChange={(values, fullForm) => {
          handleValuesChange(fullForm)
        }}
      >
        <div style={{ padding: '24px 0', background: hexToRGBA(token.colorBgLayout, 0.3) }}>
          <Row gutter={[32, 32]}>
            {/* Left Column - Clinic Identity */}
            <Col xs={24} lg={10}>
              <Card
                bordered={false}
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                  border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`
                }}
                bodyStyle={{ padding: '32px' }}
              >
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                  <Title
                    level={3}
                    style={{
                      margin: '0 0 8px 0',
                      color: token.colorPrimary,
                      fontSize: '24px',
                      fontWeight: '600'
                    }}
                  >
                    Facility Identity
                  </Title>
                  <Text type='secondary' style={{ fontSize: '14px' }}>
                    Configure your facility's brand and basic information
                  </Text>
                </div>

                {/* Logo Upload Section */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '32px'
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <Tooltip title='Click to upload facility logo' placement='top'>
                      <input
                        type='file'
                        ref={inputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        accept='image/*'
                      />
                      <div
                        style={{
                          display: 'grid',
                          width: '160px',
                          height: '160px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          position: 'relative'
                        }}
                      >
                        <Avatar
                          src={image}
                          alt='Facility Logo'
                          size={160}
                          style={{
                            gridColumn: 1,
                            gridRow: 1,
                            backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                            border: `3px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            transition: 'all 0.3s ease'
                          }}
                          icon={<FontAwesomeIcon icon={faClinicMedical} size='3x' />}
                        />
                        <div
                          style={{
                            ...imageOverlayStyle({ loading, token }),
                            borderRadius: '50%',
                            width: '160px',
                            height: '160px',
                            opacity: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'opacity 0.3s ease'
                          }}
                          className='logo-overlay'
                          onClick={loading ? undefined : handleUploadClick}
                        >
                          <FontAwesomeIcon color={token.colorWhite} icon={faEdit} size='2x' />
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                </div>

                {/* Clinic Name */}
                <Form.Item
                  name={Templates.dataModel.name.key}
                  label={
                    <Space>
                      <Text strong style={{ fontSize: '16px' }}>
                        {Templates.dataModel.name.label}
                      </Text>
                      <Tooltip title='This will be the display name of the facility' placement='top'>
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          style={{ color: token.colorPrimary, fontSize: '12px', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: Templates.dataModel.name.required,
                      message: Templates.dataModel.name.validationMsg
                    }
                  ]}
                  style={{ marginBottom: '24px' }}
                >
                  <Input
                    size='large'
                    onChange={(e) => {
                      specialFieldValidation(basicInfoForm, specialFormState, setSpecialFormState, {
                        key: 'name',
                        event: e,
                        validationConfig: {
                          letters: { allowed: true, onlyCaps: false },
                          numbers: false,
                          spaces: true
                        }
                      })
                    }}
                    maxLength={50}
                    placeholder={Templates.dataModel.name.placeholder}
                    prefix={
                      <FontAwesomeIcon
                        icon={faClinicMedical}
                        style={{ color: token.colorPrimary, marginRight: '8px' }}
                      />
                    }
                    style={{
                      borderRadius: '8px',
                      fontSize: '16px',
                      padding: '12px 16px'
                    }}
                  />
                </Form.Item>
                <Form.Item
                  hidden={true}
                  name={Templates.dataModel.termsConditions.key}
                  label={
                    <Space>
                      <Text strong style={{ fontSize: '16px' }}>
                        {Templates.dataModel.name.label}
                      </Text>
                      <Tooltip title='This will be the display name of the facility' placement='top'>
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          style={{ color: token.colorPrimary, fontSize: '12px', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message:
                        'Terms and Conditions are required for your facility. Please create comprehensive terms that outline appointment policies, payment procedures, cancellation policies, and patient responsibilities.'
                    }
                  ]}
                  style={{ marginBottom: '24px' }}
                >
                  <Input
                    size='large'
                    maxLength={50}
                    placeholder={Templates.dataModel.name.placeholder}
                    prefix={
                      <FontAwesomeIcon
                        icon={faClinicMedical}
                        style={{ color: token.colorPrimary, marginRight: '8px' }}
                      />
                    }
                    style={{
                      borderRadius: '8px',
                      fontSize: '16px',
                      padding: '12px 16px'
                    }}
                  />
                </Form.Item>
                <Divider style={{ margin: '24px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                {/* Clinic Type */}
                <Form.Item
                  name={Templates.dataModel.clinicType.key}
                  label={
                    <Text strong style={{ fontSize: '16px' }}>
                      {Templates.dataModel.clinicType.label}
                    </Text>
                  }
                  rules={[
                    {
                      required: Templates.dataModel.clinicType.required,
                      message: Templates.dataModel.clinicType.validationMsg
                    }
                  ]}
                >
                  <Radio.Group style={{ width: '100%' }}>
                    <Row gutter={[16, 16]}>
                      {/* Row 1 */}
                      <Col span={12}>
                        <Radio.Button
                          value='Medical Facility'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon
                              icon={faClinicMedical}
                              style={{ color: token.colorPrimary, fontSize: '16px' }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>Medical Facility</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                General medical practice
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>
                      <Col span={12}>
                        <Radio.Button
                          value='Specialist Practice'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon icon={faUserMd} style={{ color: token.colorPrimary, fontSize: '16px' }} />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>Specialist Practice</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                Specialized medical care
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>

                      {/* Row 2 */}
                      <Col span={12}>
                        <Radio.Button
                          value='Hospital'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon
                              icon={faHospital}
                              style={{ color: token.colorPrimary, fontSize: '16px' }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>Hospital</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                Full-service hospital facility
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>
                      <Col span={12}>
                        <Radio.Button
                          value='Primary Care Clinic'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon
                              icon={faHeartPulse}
                              style={{ color: token.colorPrimary, fontSize: '16px' }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>Primary Care Clinic</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                First-contact healthcare
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>

                      {/* Row 3 */}
                      <Col span={12}>
                        <Radio.Button
                          value='Frail Care Centre'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon
                              icon={faWheelchair}
                              style={{ color: token.colorPrimary, fontSize: '16px' }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>Frail Care Centre</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                Elderly and frail care
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>
                      <Col span={12}>
                        <Radio.Button
                          value='Workplace Clinic'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon
                              icon={faBriefcase}
                              style={{ color: token.colorPrimary, fontSize: '16px' }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>Workplace Clinic</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                Occupational healthcare
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>

                      {/* Row 4 */}
                      <Col span={12}>
                        <Radio.Button
                          value='School Clinic'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon icon={faSchool} style={{ color: token.colorPrimary, fontSize: '16px' }} />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>School Clinic</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                Educational institution care
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>
                      <Col span={12}>
                        <Radio.Button
                          value='Third Party On-site Clinic'
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          className='clinic-type-radio'
                        >
                          <Space size={12}>
                            <FontAwesomeIcon
                              icon={faBuilding}
                              style={{ color: token.colorPrimary, fontSize: '16px' }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>Third Party On-site</div>
                              <Text type='secondary' style={{ fontSize: '12px' }}>
                                External clinic on-site
                              </Text>
                            </div>
                          </Space>
                        </Radio.Button>
                      </Col>
                    </Row>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Col>

            {/* Right Column - Configuration */}
            <Col xs={24} lg={14}>
              <Row gutter={[24, 24]}>
                {/* Clinic Status */}
                <Col span={24}>
                  <Card
                    title={
                      <Space size={12}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FontAwesomeIcon icon={faCog} style={{ color: token.colorPrimary, fontSize: '18px' }} />
                        </div>
                        <div>
                          <Text strong style={{ fontSize: '18px', display: 'block' }}>
                            Facility Status
                          </Text>
                          <Text type='secondary' style={{ fontSize: '14px' }}>
                            Configure operational settings
                          </Text>
                        </div>
                      </Space>
                    }
                    bordered={false}
                    style={{
                      borderRadius: '16px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                    headStyle={{
                      borderBottom: 'none',
                      backgroundColor: 'transparent',
                      padding: '24px 24px 0 24px'
                    }}
                  >
                    <Row gutter={[20, 20]}>
                      <Col xs={24} md={8}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.15)}`,
                            backgroundColor: hexToRGBA(token.colorPrimary, 0.04),
                            textAlign: 'center',
                            height: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{ padding: '20px 16px' }}
                        >
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '16px',
                              fontSize: '15px',
                              color: token.colorPrimary
                            }}
                          >
                            {Templates.dataModel.isActive.label}
                          </Text>
                          <Form.Item
                            style={{ margin: '0' }}
                            name={Templates.dataModel.isActive.key}
                            valuePropName='checked'
                          >
                            <Switch
                              checkedChildren='Active'
                              unCheckedChildren='Inactive'
                              onChange={handleIsActive}
                              size='default'
                              style={{
                                minWidth: '70px'
                              }}
                              className='custom-active-switch'
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                      <Col xs={24} md={8}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorSuccess, 0.15)}`,
                            backgroundColor: hexToRGBA(token.colorSuccess, 0.04),
                            textAlign: 'center',
                            height: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{ padding: '20px 16px' }}
                        >
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '16px',
                              fontSize: '15px',
                              color: token.colorSuccess
                            }}
                          >
                            {Templates.dataModel.clinicBookings.label}
                          </Text>
                          <Form.Item
                            style={{ margin: '0' }}
                            name={Templates.dataModel.clinicBookings.key}
                            valuePropName='checked'
                          >
                            <Switch
                              checkedChildren='Yes'
                              unCheckedChildren='No'
                              size='default'
                              style={{ minWidth: '70px' }}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                      <Col xs={24} md={8}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorInfo, 0.15)}`,
                            backgroundColor: hexToRGBA(token.colorInfo, 0.04),
                            textAlign: 'center',
                            height: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{ padding: '20px 16px' }}
                        >
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '16px',
                              fontSize: '15px',
                              color: token.colorInfo
                            }}
                          >
                            {Templates.dataModel.homeVisits.label}
                          </Text>
                          <Form.Item
                            style={{ margin: '0' }}
                            name={Templates.dataModel.homeVisits.key}
                            valuePropName='checked'
                          >
                            <Switch
                              checkedChildren='Yes'
                              unCheckedChildren='No'
                              size='default'
                              style={{ minWidth: '70px' }}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                      {/* Telehealth Services Card */}
                      <Col xs={24} md={8}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorWarning, 0.15)}`,
                            backgroundColor: hexToRGBA(token.colorWarning, 0.04),
                            textAlign: 'center',
                            height: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{ padding: '20px 16px' }}
                        >
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '16px',
                              fontSize: '15px',
                              color: token.colorWarning
                            }}
                          >
                            {Templates.dataModel.healthTele.label}
                          </Text>
                          <Form.Item
                            style={{ margin: '0' }}
                            name={Templates.dataModel.healthTele.key}
                            key={Templates.dataModel.healthTele.key}
                            valuePropName='checked'
                          >
                            <Switch
                              checkedChildren='Yes'
                              unCheckedChildren='No'
                              size='default'
                              style={{ minWidth: '70px' }}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                      {/* Vitals Monitoring Card */}
                      <Col xs={24} md={8}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA('#20b2aa', 0.15)}`,
                            backgroundColor: hexToRGBA('#20b2aa', 0.04),
                            textAlign: 'center',
                            height: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{ padding: '20px 16px' }}
                        >
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '16px',
                              fontSize: '15px',
                              color: '#20b2aa'
                            }}
                          >
                            {Templates.dataModel.vitalsMonitoring.label}
                          </Text>
                          <Form.Item
                            style={{ margin: '0' }}
                            name={Templates.dataModel.vitalsMonitoring.key}
                            valuePropName='checked'
                          >
                            <Switch
                              checkedChildren='Yes'
                              unCheckedChildren='No'
                              size='default'
                              style={{ minWidth: '70px' }}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                      {/* Walk-ins Service Card */}
                      <Col xs={24} md={8}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA('#8e44ad', 0.15)}`,
                            backgroundColor: hexToRGBA('#8e44ad', 0.04),
                            textAlign: 'center',
                            height: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{ padding: '20px 16px' }}
                        >
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '16px',
                              fontSize: '15px',
                              color: '#8e44ad'
                            }}
                          >
                            {Templates.dataModel.walkinsAllowed.label}
                          </Text>
                          <Form.Item
                            style={{ margin: '0' }}
                            name={Templates.dataModel.walkinsAllowed.key}
                            valuePropName='checked'
                          >
                            <Switch
                              checkedChildren='Yes'
                              unCheckedChildren='No'
                              size='default'
                              style={{ minWidth: '70px' }}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                {/* Payment Methods */}
                <Col span={24}>
                  <Card
                    title={
                      <Space size={12}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: hexToRGBA(token.colorSuccess, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FontAwesomeIcon icon={faMoneyBill} style={{ color: token.colorSuccess, fontSize: '18px' }} />
                        </div>
                        <div>
                          <Text strong style={{ fontSize: '18px', display: 'block' }}>
                            Payment Methods
                          </Text>
                          <Text type='secondary' style={{ fontSize: '14px' }}>
                            Select accepted payment options
                          </Text>
                        </div>
                      </Space>
                    }
                    bordered={false}
                    style={{
                      borderRadius: '16px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f0fff4 100%)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                    headStyle={{
                      borderBottom: 'none',
                      backgroundColor: 'transparent',
                      padding: '24px 24px 0 24px'
                    }}
                  >
                    <Form.Item
                      style={{ margin: 0 }}
                      name={Templates.dataModel.paymentMethods.key}
                      rules={[
                        {
                          required: Templates.dataModel.paymentMethods.required,
                          message: Templates.dataModel.paymentMethods.validationMsg
                        }
                      ]}
                    >
                      <Checkbox.Group style={{ width: '100%' }}>
                        <Row gutter={[16, 16]}>
                          {paymentMethods.map((option) => (
                            <Col key={option} xs={24} sm={12} md={8}>
                              <div style={{ position: 'relative' }}>
                                <Checkbox
                                  value={option}
                                  style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    transform: 'scale(1.2)',
                                    zIndex: 2,
                                    pointerEvents: 'none'
                                  }}
                                />
                                <Card
                                  size='small'
                                  style={{
                                    borderRadius: '12px',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    height: '100px',
                                    border: `2px solid ${hexToRGBA(token.colorBorder, 0.2)}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                  }}
                                  className='payment-method-card'
                                  bodyStyle={{
                                    padding: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    width: '100%'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    // Find the hidden checkbox and trigger its click
                                    const checkbox =
                                      e.currentTarget.parentElement.querySelector('input[type="checkbox"]')
                                    if (checkbox) {
                                      checkbox.click()
                                    }
                                  }}
                                >
                                  {/* Center content - Icon and Text */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: '8px',
                                      flex: 1
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={getPaymentIcon(option)}
                                      style={{
                                        fontSize: '28px',
                                        color: token.colorPrimary
                                      }}
                                    />
                                    <Text
                                      style={{
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        textAlign: 'center'
                                      }}
                                    >
                                      {option}
                                    </Text>
                                  </div>
                                </Card>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </Card>
                </Col>

                {/* After Hours Fee */}
                <Col span={24}>
                  <Card
                    title={
                      <Space size={12}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: hexToRGBA(token.colorWarning, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FontAwesomeIcon icon={faMoneyBill} style={{ color: token.colorWarning, fontSize: '18px' }} />
                        </div>
                        <div>
                          <Text strong style={{ fontSize: '18px', display: 'block' }}>
                            After Hours Pricing
                          </Text>
                          <Text type='secondary' style={{ fontSize: '14px' }}>
                            Set additional fees for after-hours consultations
                          </Text>
                        </div>
                      </Space>
                    }
                    bordered={false}
                    style={{
                      borderRadius: '16px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fffbf0 100%)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                    headStyle={{
                      borderBottom: 'none',
                      backgroundColor: 'transparent',
                      padding: '24px 24px 0 24px'
                    }}
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={12}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorWarning, 0.15)}`,
                            backgroundColor: hexToRGBA(token.colorWarning, 0.04),
                            textAlign: 'center',
                            height: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{
                            padding: '20px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%'
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faCoins}
                            style={{
                              fontSize: '28px',
                              color: token.colorWarning,
                              marginBottom: '12px'
                            }}
                          />
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '12px',
                              fontSize: '15px',
                              color: token.colorWarning
                            }}
                          >
                            {Templates.dataModel.afterHourFee.label}
                          </Text>
                          <Form.Item style={{ margin: '0', width: '100%' }} name={Templates.dataModel.afterHourFee.key}>
                            <InputNumber
                              addonBefore='R'
                              placeholder='0.00'
                              min={0}
                              step={0.01}
                              precision={2}
                              size='large'
                              style={{
                                width: '100%',
                                borderRadius: '8px'
                              }}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                      <Col xs={24} md={12}>
                        <div
                          style={{
                            height: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: '0 16px'
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: '16px',
                              marginBottom: '12px',
                              color: token.colorText
                            }}
                          >
                            About After Hours Fees
                          </Text>
                          <Text
                            type='secondary'
                            style={{
                              fontSize: '14px',
                              lineHeight: '1.6',
                              marginBottom: '8px'
                            }}
                          >
                            Additional charges applied to consultations outside of regular business hours.
                          </Text>
                          <Text
                            type='secondary'
                            style={{
                              fontSize: '13px',
                              fontStyle: 'italic'
                            }}
                          >
                            This fee will be added to the standard consultation fee for appointments scheduled after
                            hours.
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                {/* Virtual Consultation Pricing */}
                <Col span={24}>
                  <Card
                    title={
                      <Space size={12}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FontAwesomeIcon icon={faCoins} style={{ color: token.colorPrimary, fontSize: '18px' }} />
                        </div>
                        <div>
                          <Text strong style={{ fontSize: '18px', display: 'block' }}>
                            Virtual Consultation Pricing
                          </Text>
                          <Text type='secondary' style={{ fontSize: '14px' }}>
                            Set the cost for virtual/telehealth consultations
                          </Text>
                        </div>
                      </Space>
                    }
                    bordered={false}
                    style={{
                      borderRadius: '16px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                    headStyle={{
                      borderBottom: 'none',
                      backgroundColor: 'transparent',
                      padding: '24px 24px 0 24px'
                    }}
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={12}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorPrimary, 0.15)}`,
                            backgroundColor: hexToRGBA(token.colorPrimary, 0.04),
                            textAlign: 'center',
                            height: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                          bodyStyle={{ padding: '20px' }}
                        >
                          <Text
                            strong
                            style={{
                              display: 'block',
                              marginBottom: '12px',
                              fontSize: '15px',
                              color: token.colorPrimary
                            }}
                          >
                            {Templates.dataModel.virtualConsultationCost.label}
                          </Text>
                          <Form.Item
                            style={{ margin: '0', width: '100%' }}
                            name={Templates.dataModel.virtualConsultationCost.key}
                          >
                            <InputNumber
                              addonBefore='R'
                              placeholder='0.00'
                              min={0}
                              step={0.01}
                              precision={2}
                              size='large'
                              style={{
                                width: '100%',
                                borderRadius: '8px'
                              }}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                      <Col xs={24} md={12}>
                        <div
                          style={{
                            height: '140px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px'
                          }}
                        >
                          <Text
                            type='secondary'
                            style={{
                              fontSize: '14px',
                              textAlign: 'center',
                              lineHeight: '1.6'
                            }}
                          >
                            This will be the standard rate charged for virtual consultations (video calls, phone calls,
                            etc.). Patients will see this price when booking telehealth appointments.
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                {/* Legal & Compliance */}
                <Col span={24}>
                  <Card
                    title={
                      <Space size={12}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: hexToRGBA(token.colorWarning, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faFileContract}
                            style={{ color: token.colorWarning, fontSize: '18px' }}
                          />
                        </div>
                        <div>
                          <Text strong style={{ fontSize: '18px', display: 'block' }}>
                            Legal & Compliance
                          </Text>
                          <Text type='secondary' style={{ fontSize: '14px' }}>
                            Manage legal documents and policies
                          </Text>
                        </div>
                      </Space>
                    }
                    bordered={false}
                    style={{
                      borderRadius: '16px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fffbf0 100%)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                    headStyle={{
                      borderBottom: 'none',
                      backgroundColor: 'transparent',
                      padding: '24px 24px 0 24px'
                    }}
                  >
                    <Row gutter={[20, 20]}>
                      <Col xs={24} md={12}>
                        <Card
                          size='small'
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${hexToRGBA(token.colorWarning, 0.15)}`,
                            backgroundColor: hexToRGBA(token.colorWarning, 0.04),
                            textAlign: 'center',
                            height: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          bodyStyle={{
                            padding: '20px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            gap: '12px'
                          }}
                          className='terms-conditions-card'
                          onClick={() => setTermsModalVisible(true)}
                        >
                          <FontAwesomeIcon
                            icon={faFileContract}
                            style={{
                              fontSize: '32px',
                              color: token.colorWarning,
                              marginBottom: '8px'
                            }}
                          />
                          <Text
                            strong
                            style={{
                              fontSize: '15px',
                              color: token.colorWarning,
                              marginBottom: '4px'
                            }}
                          >
                            Terms & Conditions
                          </Text>
                          <Text type='secondary' style={{ fontSize: '12px', textAlign: 'center' }}>
                            {/* {termsContent ? 'Edit existing terms' : 'Create terms & conditions'} */}
                          </Text>
                          <Button
                            type='primary'
                            size='small'
                            style={{
                              marginTop: '8px',
                              backgroundColor: token.colorWarning,
                              borderColor: token.colorWarning
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              setTermsModalVisible(true)
                            }}
                          >
                            {/* {termsContent ? 'Edit Terms' : 'Create Terms'} */}
                          </Button>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Terms & Conditions Modal */}
          <TermsConditionsModal
            visible={termsModalVisible}
            onClose={() => setTermsModalVisible(false)}
            token={token}
            basicInfoForm={basicInfoForm}
            onSave={(t) => {
              basicInfoForm.setFieldValue('termsConditions', t)
              handleValuesChange({ termsConditions: t })
            }}
          />

          <style jsx global>{`
            .logo-overlay:hover {
              opacity: 1 !important;
            }

            .payment-method-card:hover {
              transform: translateY(-2px);
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
              border-color: ${token.colorPrimary} !important;
            }

            .payment-method-card .ant-checkbox-wrapper-checked {
              color: ${token.colorPrimary};
            }

            /* Target the card when its sibling checkbox is checked */
            .payment-method-card:has(+ .ant-checkbox-wrapper-checked),
            .ant-checkbox-wrapper-checked + .payment-method-card,
            .payment-method-card:has(.ant-checkbox-wrapper-checked) {
              border-color: ${token.colorPrimary} !important;
              background: ${hexToRGBA(token.colorPrimary, 0.08)} !important;
              box-shadow: 0 4px 20px rgba(24, 144, 255, 0.15) !important;
            }

            /* Style for when the checkbox input is checked */
            input[type='checkbox']:checked + .payment-method-card,
            .payment-method-card:has(input[type='checkbox']:checked) {
              border-color: ${token.colorPrimary} !important;
              background: ${hexToRGBA(token.colorPrimary, 0.08)} !important;
              box-shadow: 0 4px 20px rgba(24, 144, 255, 0.15) !important;
            }

            /* Handle the parent container when checkbox is checked */
            div:has(> .ant-checkbox-wrapper-checked) .payment-method-card {
              border-color: ${token.colorPrimary} !important;
              background: ${hexToRGBA(token.colorPrimary, 0.08)} !important;
              box-shadow: 0 4px 20px rgba(24, 144, 255, 0.15) !important;
            }

            .clinic-type-radio.ant-radio-button-wrapper-checked {
              background: ${hexToRGBA(token.colorPrimary, 0.1)} !important;
              border-color: ${token.colorPrimary} !important;
              box-shadow: 0 4px 15px rgba(24, 144, 255, 0.2) !important;
            }

            .clinic-type-radio:hover {
              transform: translateY(-1px);
              box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
            }

            .terms-conditions-card:hover {
              transform: translateY(-2px);
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
              border-color: ${token.colorWarning} !important;
            }

            .custom-active-switch.ant-switch-checked {
              background-color: #52c41a !important;
            }

            .custom-active-switch:not(.ant-switch-checked) {
              background-color: #ff4d4f !important;
            }
          `}</style>
        </div>{' '}
      </Form>
    )
  }
}
